import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Collapse,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Paper,
    CircularProgress
} from '@mui/material';
import { Container, Modal, Button, Row as BootstrapRow, Col as BootstrapCol, Form } from 'react-bootstrap';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

// Assuming these actions and API helpers are correctly set up
import { fetchDummyWallets } from '../../Actions/adminActions';
import apiCall from '../../API/API';
import PaginationComponent from '../../Component/Pagination/PaginationControls';
import { identity } from 'lodash';

// A single, expandable row in the table
const RowComponent = ({ user, srNo, onUpdateClick }) => {
    const [open, setOpen] = useState(false);

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{srNo}</TableCell>
                <TableCell component="th" scope="row" sx={{ whiteSpace: 'nowrap' }}>{user.username}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{user.name}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{user.uid}</TableCell>
                <TableCell align="center" sx={{ minWidth: 100 }}>
                    <Button variant="info" size="sm" onClick={() => onUpdateClick(user)}>
                        Update
                    </Button>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1, padding: 2, backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                            <Typography variant="h6" gutterBottom component="div">
                                Team Levels
                            </Typography>
                            <TableContainer>
                                <Table size="small" aria-label="team-levels">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>Level</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>Total Team</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>Active Team</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>Business</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {user.teamSection && user.teamSection.length > 0 ? (
                                            user.teamSection.map((levelData) => (
                                                <TableRow key={levelData.level}>
                                                    <TableCell>{levelData.level}</TableCell>
                                                    <TableCell>{levelData.total_team}</TableCell>
                                                    <TableCell>{levelData.active_team}</TableCell>
                                                    <TableCell>{levelData.business}</TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">No team data available.</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};


// The main component for the entire page
const TeamManagementTable = () => {
    const dispatch = useDispatch();
    const { dummyWallets, loading, error } = useSelector((state) => state.admin);

    // --- STATE MANAGEMENT ---
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit, setLimit] = useState(10);
    const [filters, setFilters] = useState({ username: '', uid: '' });

    // Modal State
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedLevel, setSelectedLevel] = useState('');
    const [levelData, setLevelData] = useState({ total_team: '', active_team: '', business: '' });
    const [isUpdating, setIsUpdating] = useState(false);

    // --- DATA FETCHING ---
    const fetchData = useCallback(() => {
        const params = {
            page: currentPage,
            limit,
            username: filters.username,
            uid: filters.uid,
            identity_user: 'fake'

        };
        Object.keys(params).forEach(key => {
            if (!params[key]) {
                delete params[key];
            }
        });
        dispatch(fetchDummyWallets(params));
    }, [dispatch, currentPage, limit, filters]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (dummyWallets) {
            setTotalPages(dummyWallets.totalPages || 1);
        }
    }, [dummyWallets]);

    // --- FILTER HANDLERS ---
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleApplyFilter = () => {
        setCurrentPage(1);
        fetchData();
    };

    const handleResetFilter = () => {
        setFilters({ username: '', uid: '' });
        setCurrentPage(1);
        dispatch(fetchDummyWallets({ page: 1, limit }));
    };

    // --- MODAL HANDLERS ---
    const handleUpdateClick = (user) => {
        setSelectedUser(user);
        setShowUpdateModal(true);
    };

    const handleModalClose = () => {
        setShowUpdateModal(false);
        setSelectedUser(null);
        setSelectedLevel('');
        setLevelData({ total_team: '', active_team: '', business: '' });
        setIsUpdating(false);
    };

    const handleLevelChange = (e) => {
        const level = e.target.value;
        setSelectedLevel(level);
        if (level && selectedUser) {
            const data = selectedUser.teamSection.find(l => String(l.level) === level);
            if (data) {
                setLevelData({
                    total_team: data.total_team,
                    active_team: data.active_team,
                    business: data.business,
                });
            }
        } else {
            setLevelData({ total_team: '', active_team: '', business: '' });
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setLevelData(prev => ({ ...prev, [name]: value }));
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        if (!selectedLevel) {
            toast.error("Please select a level to update.");
            return;
        }
        setIsUpdating(true);
        try {
            const payload = {
                uid: selectedUser.uid,
                level: selectedLevel,
                total_team: levelData.total_team,
                active_team: levelData.active_team,
                business: levelData.business,
            };
            const res = await apiCall("POST", `/update_fake_user_wallet`, payload);

            if (res.data.success) {
                toast.success(res.data.message || "Team level updated successfully!");
                handleModalClose();
                fetchData();
            } else {
                 toast(res?.data?.message || "Failed to update team level.");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "An error occurred.");
            console.error("Update failed:", err);
        } finally {
            setIsUpdating(false);
        }
    };


    return (
        <Container fluid className="mt-4">
            <Typography variant="h4" gutterBottom>
                Team Management
            </Typography>

            <Paper sx={{ padding: 2, marginBottom: 2 }}>
                <Typography variant="h6" gutterBottom>Filters</Typography>
                <BootstrapRow className="align-items-end g-3">
                    <BootstrapCol xs={12} md={4}>
                        <Form.Group>
                            <Form.Label>Username</Form.Label>
                            <Form.Control type="text" name="username" placeholder="Filter by username" value={filters.username} onChange={handleFilterChange} />
                        </Form.Group>
                    </BootstrapCol>
                    <BootstrapCol xs={12} md={4}>
                        <Form.Group>
                            <Form.Label>User ID (UID)</Form.Label>
                            <Form.Control type="text" name="uid" placeholder="Filter by UID" value={filters.uid} onChange={handleFilterChange} />
                        </Form.Group>
                    </BootstrapCol>
                    <BootstrapCol xs={12} md="auto" className="d-flex align-self-end gap-2 mt-3 mt-md-0">
                        <Button variant="primary" onClick={handleApplyFilter}>Apply</Button>
                        <Button variant="secondary" onClick={handleResetFilter}>Reset</Button>
                    </BootstrapCol>
                </BootstrapRow>
            </Paper>

            <TableContainer component={Paper}>
                <Table aria-label="collapsible table">
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            {/* ✅ Added whiteSpace to prevent wrapping */}
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>Sr. No.</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>Username</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>Name</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>UID</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center"><CircularProgress /></TableCell>
                            </TableRow>
                        ) : (
                            dummyWallets?.data?.map((user, index) => (
                                <RowComponent
                                    key={user.uid}
                                    user={user}
                                    srNo={(currentPage - 1) * limit + index + 1}
                                    onUpdateClick={handleUpdateClick}
                                />
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <PaginationComponent
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => setCurrentPage(page)}
            />

            <Modal show={showUpdateModal} onHide={handleModalClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Update Team Level</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedUser && (
                        <Form onSubmit={handleUpdateSubmit}>
                            <p><strong>Username:</strong> {selectedUser.username}</p>
                            <hr />
                            <Form.Group as={BootstrapRow} className="mb-3">
                                <Form.Label column xs={12} sm={4}>Select Level</Form.Label>
                                <BootstrapCol xs={12} sm={8}>
                                    <Form.Select value={selectedLevel} onChange={handleLevelChange} required>
                                        <option value="">-- Select a Level --</option>
                                        {selectedUser.teamSection.map(l => (
                                            <option key={l.level} value={l.level}>Level {l.level}</option>
                                        ))}
                                    </Form.Select>
                                </BootstrapCol>
                            </Form.Group>

                            {selectedLevel && (
                                <>
                                    <Form.Group as={BootstrapRow} className="mb-3">
                                        <Form.Label column xs={12} sm={4}>Total Team</Form.Label>
                                        <BootstrapCol xs={12} sm={8}>
                                            <Form.Control type="number" name="total_team" value={levelData.total_team} onChange={handleInputChange} />
                                        </BootstrapCol>
                                    </Form.Group>
                                    <Form.Group as={BootstrapRow} className="mb-3">
                                        <Form.Label column xs={12} sm={4}>Active Team</Form.Label>
                                        <BootstrapCol xs={12} sm={8}>
                                            <Form.Control type="number" name="active_team" value={levelData.active_team} onChange={handleInputChange} />
                                        </BootstrapCol>
                                    </Form.Group>
                                    <Form.Group as={BootstrapRow} className="mb-3">
                                        <Form.Label column xs={12} sm={4}>Business</Form.Label>
                                        <BootstrapCol xs={12} sm={8}>
                                            <Form.Control type="number" name="business" value={levelData.business} onChange={handleInputChange} />
                                        </BootstrapCol>
                                    </Form.Group>
                                </>
                            )}

                            <div className="d-flex justify-content-end mt-4">
                                <Button variant="secondary" onClick={handleModalClose} className="me-2">Cancel</Button>
                                <Button variant="primary" type="submit" disabled={isUpdating || !selectedLevel}>
                                    {isUpdating ? 'Saving...' : 'Save Changes'}
                                </Button>
                            </div>
                        </Form>
                    )}
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default TeamManagementTable;
